import React, { useState, useEffect } from 'react'

// UI Component imports from Ant Design and other libraries
import CustomRow from '../../../reusable-components/CustomRow'
import {
  Button,
  Card,
  Col,
  Empty,
  Modal,
  Row,
  Space,
  Spin,
  theme,
  Typography,
  Divider,
  Tooltip,
  Popconfirm
} from 'antd'

import CustomButton from '../../../reusable-components/CustomButton'
import ReactQuill from 'react-quill'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChainBroken,
  faCheckCircle,
  faCircleXmark,
  faNoteSticky,
  faInfoCircle,
  faArchive
} from '@fortawesome/free-solid-svg-icons'

// Custom component imports for various medical functionalities
import DiagnosisCapture from './components/DiagnosisCapture'
import ExaminationProceduresPerformed from '../examination-procedures-performed'

import ClinicalNotesTemplates from './components/ClinicalNotesTemplates'

import CustomLoadingIcon from '../../../reusable-components/CustomLoadingIcon'

// Utility and helper function imports
import { hexToRGBA } from '../../../lib/utils'
import { isReception, isAdmin } from '../../../lib/profile-utils'

// Style imports
import './notes.css'
import '../../../Examination/examination-utils/style.css'
import ClinicalNotesToolbar from './components/ClinicalNotesToolbar'
import HistoricalNotes from './components/HistoricalNotes'
import { useNoteState } from './hooks/useNoteState'
import TranscribeAi from './components/TranscribeAI'
import AIGenerateModal from './components/AIGenerateModal'
import ClinicalTemplates from './components/clinical templates/ClinicalTemplates'
import Imaging from '../Assessment/Imaging'

const { Title } = Typography

// Memoized components to prevent unnecessary re-renders
const MemoizedDiagnosisCapture = React.memo(DiagnosisCapture)
const MemoizedReactQuill = React.memo(ReactQuill)

// Quill editor modules and formats
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean']
  ]
}

const quillFormats = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet']

// Reception/Admin Read-Only Component
const ClinicalNotesReadOnly = ({ bookingData, userRef, setModalOpen, noBooking }) => {
  const { token } = theme.useToken()
  const { noteState } = useNoteState({ bookingData, userRef })
  const [showHistorical, setShowHistorical] = useState(true)

  return (
    <CustomRow style={{ marginTop: 12 }}>
      {noBooking && (
        <Col span={24}>
          <FontAwesomeIcon
            size='2x'
            color={token.colorError}
            style={{ float: 'right', cursor: 'pointer' }}
            onClick={() => setModalOpen(false)}
            icon={faCircleXmark}
          />
        </Col>
      )}
      <Col span={24} style={{ minHeight: '100%', position: 'relative' }}>
        <Card
          styles={{
            header: {
              background: hexToRGBA(token.colorPrimary, 0.08),
              borderBottom: `1px solid ${hexToRGBA(token.colorPrimary, 0.1)}`,
              padding: '12px 16px'
            },
            body: { padding: 0 }
          }}
          size='small'
          style={{
            width: '100%',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden',
            marginBottom: '16px'
          }}
          title={
            <Space>
              <FontAwesomeIcon
                icon={faNoteSticky}
                style={{
                  color: token.colorPrimary,
                  fontSize: '16px'
                }}
              />
              <Title level={5} style={{ margin: 0 }}>
                Clinical Notes (Read Only)
              </Title>
            </Space>
          }
        >
          <HistoricalNotes
            bookingData={bookingData}
            userRef={userRef}
            noteState={noteState}
            setShowHistorical={setShowHistorical}
          />
        </Card>
      </Col>
    </CustomRow>
  )
}

// Full Clinical Notes Component for Medical Professionals
const ClinicalNotesFullAccess = ({
  bookingData,
  userRef,
  noBooking,
  setModalOpen,
  setBookingData,
  websocketConnection
}) => {
  // Redux state and theme access
  const { token } = theme.useToken()
  const [instructionsModalVisible, setInstructionsModalVisible] = useState(false)
  const [showAIGenerateModal, setShowAIGenerateModal] = useState(false)

  const {
    noteState,
    setNoteState,
    showClinicalNotesTemplates,
    setShowClinicalNotesTemplates,
    showHistorical,
    setShowHistorical,
    checkForExistingNote,
    handleCreateNote,
    handleUpdateNote,
    showAiTranscription,
    setShowAiTranscription,
    handleAutoGenerateNotes,
    transcriptionState,
    debouncedNoteCapture
  } = useNoteState({ bookingData, userRef })

  // Automatically create a note if one doesn't exist (but only after checking for existing notes)
  useEffect(() => {
    if (
      !noBooking &&
      !noteState.noteId &&
      !noteState.isCreating &&
      !noteState.isSubmitting &&
      bookingData?._id &&
      !noteState.fetchingError
    ) {
      // Only create note if we've finished checking for existing notes and found none
      if (!noteState.isSubmitting) {
        handleCreateNote()
      }
    }
  }, [
    noteState.noteId,
    noteState.isCreating,
    noteState.isSubmitting,
    noteState.fetchingError,
    noBooking,
    bookingData?._id,
    handleCreateNote
  ])

  const handleShowAIGenerateModal = () => {
    setShowAIGenerateModal(true)
  }

  const handleAIGenerateCancel = () => {
    setShowAIGenerateModal(false)
  }

  const handleAIGenerate = async (options) => {
    await handleAutoGenerateNotes(options)
    setShowAIGenerateModal(false)
  }

  const handleTemplateInsert = (template) => {
    if (noteState.noteId && template.content) {
      // Insert template content into the current note
      const currentContent = noteState.currentNote || ''
      const newContent = currentContent + '\n\n' + template.content
      setNoteState((prev) => ({ ...prev, currentNote: newContent, isUpdating: true }))
      debouncedNoteCapture(noteState.noteId, newContent)
    }
  }

  const handleTemplateGenerate = (template) => {
    // Handle template generation - could save to a templates database
    // For now, just show a success message or handle as needed
  }

  const renderCurrentNote = () => (
    <Row>
      <Col span={24}>
        <div className='quill-wrapper' style={{ width: '100%', height: '100%', position: 'relative' }}>
          <CustomLoadingIcon
            loading={noteState.isAutoGenerating || noteState.isSubmitting || noteState.isCreating}
            loadingText={
              noteState.isCreating
                ? 'Creating Note...'
                : noteState.isAutoGenerating
                ? 'AI is Generating Clinical Notes...'
                : 'Loading Notes...'
            }
            content={
              <div style={{ position: 'relative' }}>
                <MemoizedReactQuill
                  key={`editor-${noteState.noteId || 'new'}`}
                  id={`${bookingData?._id}-notes-capture-area`}
                  style={{
                    display: showHistorical ? 'none' : '',
                    width: '100%',
                    height: 'auto',
                    position: 'relative',
                    zIndex: 2
                  }}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Start typing your notes here. Your notes save automatically. To create a new note, use the 'Archive & New' button."
                  onChange={(e) => {
                    if (!noteState.isCreating && noteState.noteId) {
                      setNoteState((prev) => ({ ...prev, isUpdating: true, currentNote: e }))
                      debouncedNoteCapture(noteState.noteId, e)
                    }
                  }}
                  value={noteState.currentNote + transcriptionState.tmpTranscript}
                  readOnly={!noteState.noteId || noteState.isCreating}
                />

                {/* Archive Button positioned in bottom right */}
                {noteState.noteId && noteState.currentNote && !showHistorical && (
                  <Tooltip title='Archive current note and create a new blank note' placement='topLeft'>
                    <Popconfirm
                      overlayStyle={{ maxWidth: 450 }}
                      onConfirm={handleCreateNote}
                      okText='I am sure'
                      title='Archive Current Note'
                      description='This will ARCHIVE your current note and create a new blank note. Your current note will be saved and accessible from the historical notes tab, but you will not be able to edit it after archiving. Are you sure you want to proceed?'
                      placement='topLeft'
                    >
                      <Button
                        type='primary'
                        size='small'
                        disabled={
                          noteState.isAutoGenerating ||
                          noteState.isUpdating ||
                          noteState.isCreating ||
                          noteState.isSubmitting
                        }
                        style={{
                          position: 'absolute',
                          bottom: '16px',
                          right: '16px',
                          zIndex: 10,
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 12px',
                          height: 'auto',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}
                      >
                        <FontAwesomeIcon icon={faArchive} style={{ fontSize: '12px' }} />
                        Archive
                      </Button>
                    </Popconfirm>
                  </Tooltip>
                )}
              </div>
            }
          />
        </div>
      </Col>
    </Row>
  )

  return (
    <>
      <CustomRow style={{ marginTop: 12 }}>
        {noBooking && (
          <Col span={24}>
            <FontAwesomeIcon
              size='2x'
              color={token.colorError}
              style={{ float: 'right', cursor: 'pointer' }}
              onClick={() => setModalOpen(false)}
              icon={faCircleXmark}
            />
          </Col>
        )}
        {!noBooking && (
          <Col span={24}>
            <MemoizedDiagnosisCapture bookingData={bookingData} setBookingData={setBookingData} />
          </Col>
        )}
        <Col span={24}>
          <Imaging bookingData={bookingData} userRef={userRef} />
        </Col>
        <Col span={24} style={{ minHeight: '100%', position: 'relative' }}>
          <div style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr auto', gap: 12 }}>
            <Row>
              <Col span={24}>
                <Card
                  styles={{
                    header: {
                      background: hexToRGBA(token.colorPrimary, 0.08),
                      borderBottom: `1px solid ${hexToRGBA(token.colorPrimary, 0.1)}`,
                      padding: '12px 16px'
                    },
                    body: !noteState.noteId ? { padding: 0 } : { padding: 0 }
                  }}
                  size='small'
                  style={{
                    width: '100%',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    overflow: 'hidden',
                    marginBottom: '16px'
                  }}
                  title={
                    <Space>
                      <FontAwesomeIcon
                        icon={faNoteSticky}
                        style={{
                          color: token.colorPrimary,
                          fontSize: '16px'
                        }}
                      />
                      <Title level={5} style={{ margin: 0 }}>
                        Clinical Notes
                      </Title>
                      <ClinicalNotesToolbar
                        handleShowAIGenerateModal={handleShowAIGenerateModal}
                        handleCreateNote={handleCreateNote}
                        setShowAiTranscription={setShowAiTranscription}
                        setShowClinicalNotesTemplates={setShowClinicalNotesTemplates}
                        noBooking={noBooking}
                        setShowHistorical={setShowHistorical}
                        noteState={noteState}
                      />
                      {!showHistorical && (
                        <Tooltip title='View Instructions'>
                          <Button
                            type='text'
                            icon={
                              <FontAwesomeIcon
                                icon={faInfoCircle}
                                style={{
                                  fontSize: '14px',
                                  color: token.colorInfo,
                                  opacity: 0.8
                                }}
                              />
                            }
                            onClick={() => setInstructionsModalVisible(true)}
                            style={{
                              marginLeft: 8,
                              border: 'none',
                              boxShadow: 'none',
                              background: 'transparent',
                              padding: '4px 8px',
                              height: 'auto',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            size='small'
                          />
                        </Tooltip>
                      )}
                    </Space>
                  }
                  extra={
                    <>
                      {!showHistorical && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {noteState.isUpdating || noteState.isSubmitting ? (
                            <Space style={{ alignItems: 'center' }}>
                              <small>{noteState.isSubmitting ? 'LOADING NOTE' : 'SAVING'}</small>
                              <Spin spinning />
                            </Space>
                          ) : (
                            <>
                              <div style={{ alignItems: 'center', display: 'flex', gap: 4 }}>
                                <small>{noteState.fetchingError || noteState.updatingError ? 'ERROR' : 'SAVED'}</small>
                                <FontAwesomeIcon
                                  fontSize={20}
                                  icon={
                                    noteState.fetchingError || noteState.updatingError ? faChainBroken : faCheckCircle
                                  }
                                  color={
                                    noteState.fetchingError || noteState.updatingError
                                      ? token.colorError
                                      : token.colorSuccess
                                  }
                                />
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </>
                  }
                >
                  {showHistorical || noBooking ? (
                    <HistoricalNotes
                      bookingData={bookingData}
                      userRef={userRef}
                      noteState={noteState}
                      setShowHistorical={setShowHistorical}
                    />
                  ) : (
                    renderCurrentNote()
                  )}
                </Card>

                {/* Clinical Templates Section */}
                {!noBooking && !showHistorical && (
                  <ClinicalTemplates
                    bookingData={bookingData}
                    userRef={userRef}
                    onTemplateGenerate={handleTemplateGenerate}
                    onTemplateInsert={handleTemplateInsert}
                  />
                )}

                {(noteState.fetchingError || noteState.updatingError) && (
                  <div
                    style={{
                      height: '100%',
                      width: '100%',
                      background: hexToRGBA(token.colorError, 0.2),
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 30
                    }}
                  >
                    <center>
                      <Space direction='vertical'>
                        <h2>Oops something went wrong.</h2>
                        <p>{noteState.fetchingError || noteState.updatingError}</p>
                        <CustomButton
                          size='small'
                          text='Retry'
                          type='success'
                          onClick={() => {
                            if (noteState.fetchingError) {
                              setNoteState((prev) => ({ ...prev, fetchingError: '', isSubmitting: true }))
                              checkForExistingNote()
                            } else {
                              setNoteState((prev) => ({ ...prev, updatingError: '', isUpdating: true }))
                              handleUpdateNote(noteState.noteId, noteState.currentNote)
                            }
                          }}
                        />
                      </Space>
                    </center>
                  </div>
                )}
              </Col>
            </Row>
          </div>
        </Col>
        {!noBooking && (
          <Col span={24} style={{ marginTop: 12 }}>
            <ExaminationProceduresPerformed bookingData={bookingData} userRef={userRef} />
          </Col>
        )}
        <TranscribeAi
          setNoteState={setNoteState}
          setModalOpen={setShowAiTranscription}
          modalOpen={showAiTranscription}
          noteState={noteState}
          debouncedNoteCapture={debouncedNoteCapture}
          userRef={userRef}
          bookingData={bookingData}
          websocketConnection={websocketConnection}
        />
        <Modal
          footer={false}
          maskClosable={false}
          closable={false}
          width={950}
          open={showClinicalNotesTemplates}
          okButtonProps={{ style: { display: 'none' } }}
          cancelButtonProps={{ style: { display: 'none' } }}
          onCancel={() => {
            setShowClinicalNotesTemplates(false)
          }}
          destroyOnClose
        >
          <ClinicalNotesTemplates
            setModalOpen={(value) => setShowClinicalNotesTemplates((prev) => value)}
            setValue={(value) => setNoteState((prev) => ({ ...prev, currentNote: value }))}
            debouncedNoteCapture={debouncedNoteCapture}
            setUpdatingNotes={(value) => setNoteState((prev) => ({ ...prev, isUpdating: value }))}
            noteId={noteState.noteId}
          />
        </Modal>

        {/* AI Generate Modal */}
        <AIGenerateModal
          visible={showAIGenerateModal}
          onCancel={handleAIGenerateCancel}
          onGenerate={handleAIGenerate}
        />
      </CustomRow>

      {/* Instructions Modal */}
      <Modal
        title={
          <Space>
            <FontAwesomeIcon icon={faInfoCircle} style={{ color: token.colorPrimary }} />
            <span>Clinical Notes Instructions</span>
          </Space>
        }
        open={instructionsModalVisible}
        onCancel={() => setInstructionsModalVisible(false)}
        footer={[
          <Button key='close' type='primary' onClick={() => setInstructionsModalVisible(false)}>
            Got it
          </Button>
        ]}
        width={600}
      >
        <div style={{ padding: '16px 0' }}>
          <h3 style={{ marginBottom: '16px' }}>How to Use Clinical Notes</h3>

          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ color: token.colorPrimary, marginBottom: '8px' }}>Getting Started</h4>
            <p>Start typing in the editor area. Your notes are saved automatically as you type.</p>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ color: token.colorPrimary, marginBottom: '8px' }}>Button Functions</h4>
            <ul style={{ paddingLeft: '20px' }}>
              <li>
                <strong>AI Generate:</strong> Use AI to automatically generate clinical notes based on patient data
              </li>
              <li>
                <strong>Voice Transcription:</strong> Convert speech to text for faster note-taking
              </li>
              <li>
                <strong>Templates:</strong> Insert pre-made templates to speed up documentation
              </li>
              <li>
                <strong>Historical:</strong> View all previous notes for this patient
              </li>
              <li>
                <strong>Archive & New:</strong> Save current note and start a fresh one
              </li>
            </ul>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ color: token.colorPrimary, marginBottom: '8px' }}>Tips</h4>
            <ul style={{ paddingLeft: '20px' }}>
              <li>Notes are automatically saved as you type</li>
              <li>Use the Archive button to create separate notes for different visits</li>
              <li>Historical notes cannot be edited once archived</li>
              <li>Templates can help maintain consistency in documentation</li>
            </ul>
          </div>
        </div>
      </Modal>
    </>
  )
}

// Main Component with Role-Based Access
const ClinicalNotes = ({ bookingData, userRef, noBooking, setModalOpen, setBookingData, websocketConnection }) => {
  // Check if user is reception or admin - they get restricted access
  const isRestrictedRole = isReception() || isAdmin()

  if (isRestrictedRole) {
    return (
      <ClinicalNotesReadOnly
        bookingData={bookingData}
        userRef={userRef}
        setModalOpen={setModalOpen}
        noBooking={noBooking}
      />
    )
  }

  // Medical professionals get full access
  return (
    <ClinicalNotesFullAccess
      bookingData={bookingData}
      userRef={userRef}
      noBooking={noBooking}
      setModalOpen={setModalOpen}
      setBookingData={setBookingData}
      websocketConnection={websocketConnection}
    />
  )
}

export default ClinicalNotes
